import React from 'react';
import moment from 'moment';
import { TFunction } from 'react-i18next';
import { FormikProps } from 'formik';
import {
  ModalView,
  withTranslation,
  Stack,
  Button,
  Information,
  Form,
  Text,
  TextFieldControl,
  Divider,
  Trans,
  formatDate,
} from '@piwikpro/ui-components';
import { withInject } from '@piwikpro/platform';
import { FormValidator } from '@piwikpro/form-crate';
import { AuditLogService } from '@piwikpro/audit-log-crate';
import { AuditLogState } from '@piwikpro/audit-log-crate/reducer';
import { regExps } from '@piwikpro/form-crate/validators/regExps';
import { SessionState } from '@piwikpro/auth-crate/reducers/session';

interface InjectedProps {
  auditLog: AuditLogService
  i18n: import('i18next').i18n
  validation: FormValidator
}

export interface Props extends InjectedProps {
  auditLogDataRetention: Required<AuditLogState['retention']>['details']
  instanceAddress: SessionState['instanceAddress']
  onClose: () => void
  t: TFunction
}

interface FormValues {
  retentionPeriod: number
  instanceAddress: string
}

export const EditAuditLogRetentionModal: React.FC<Props> = ({
  auditLogDataRetention,
  instanceAddress,
  auditLog,
  onClose,
  validation,
  i18n,
  t,
}) => {
  const maxRetention = auditLogDataRetention!.maxPeriod;
  const currentRetention = auditLogDataRetention!.currentPeriod;
  const oldestEventFormatted = formatDate(
    new Date(auditLogDataRetention?.oldestEvent || Date.now()),
    { format: 'L' },
  );
  const handleSubmit = async (values: FormValues, api: FormikProps<FormValues>) => {
    try {
      await auditLog.setRetention(values.retentionPeriod);
      await auditLog.getRetention();
      onClose();
    // eslint-disable-next-line no-empty
    } catch {}

    api.setSubmitting(false);
  };
  return (
    <Form
      id="license-details-audit-log-data-retention-edit"
      onSubmit={handleSubmit}
      initialValues={{
        retentionPeriod: currentRetention,
        instanceAddress: '',
      }}
      validationSchema={validation.createValidationSchema<FormValues>(() => ({
      // @ts-ignore:next-line
        retentionPeriod: validation.getYup()
          .number()
          // @ts-ignore:next-line
          .notEqualTo(currentRetention, i18n.t('platform.subscription.audit-log-retention-edit.validation-must-be-not-equal.info'))
          .min(3, i18n.t('platform.subscription.audit-log-retention-edit.validation-min.info', { value: 3 }))
          .max(maxRetention, i18n.t('platform.subscription.audit-log-retention-edit.validation-max-paying.info', { value: maxRetention })),
        // @ts-ignore:next-line
        instanceAddress: validation.getYup().string()
          .required()
          // this value may include special characters,
          // which we want to exclude from regexp during matching values
          .matches(new RegExp(`^${instanceAddress.replace(regExps.specialCharactersToReplace, '\\$&')}$`, 'g'), i18n.t('administration:deleteConfirm.withCheck.notMatch')),
      }))}
      render={api => (
        <ModalView
          dataId="license-details-audit-log-data-retention-edit"
          title={t('platform.license.data-retention.audit-log-edit.title')}
          actions={(
            <Stack spacing="narrow" horizontalAlignment="right" verticalAlignment="center">
              <Button
                type="button"
                text={t('components:cancel')}
                appearance="default"
                onClick={onClose}
              />
              <Form.Submit
                text={t('components:save')}
                appearance="destructive"
              />
            </Stack>
          )}
          onClose={onClose}
        >
          <Stack vertical>
            <Stack.Item fill>
              <Information type="warning">
                {t('platform.license.data-retention.audit-log-edit.warning.info', { oldestEvent: oldestEventFormatted, count: moment().diff(auditLogDataRetention!.oldestEvent, 'months') || 1 })}
              </Information>
            </Stack.Item>
            <Stack.Item fill>
              <Stack verticalAlignment="center" spacing="extraNarrow">
                <Stack.Item fill>
                  <TextFieldControl
                    label={t('platform.license.data-retention.audit-log-edit.form.period.label')}
                    info={t('platform.license.data-retention.audit-log-edit.form.period.info', { maxValue: maxRetention })}
                    name="retentionPeriod"
                    type="number"
                    onKeyDown={(e) => {
                      if (['Backspace', 'Tab', 'ArrowLeft', 'ArrowRight'].includes(e.key)) {
                        return true;
                      }
                      if (!/[0-9]/.test(e.key)) {
                        e.preventDefault();
                      }

                      return true;
                    }}
                    autoFocus
                  />
                </Stack.Item>
                <Stack.Item>
                  <Button
                    disabled={api.values.retentionPeriod === maxRetention}
                    text={t('platform.license.data-retention.audit-log-edit.form.period.setMax')}
                    onClick={() => api.setFieldValue('retentionPeriod', maxRetention)}
                  />
                </Stack.Item>
              </Stack>
            </Stack.Item>
            <Stack.Item fill>
              <Divider noMargin />
            </Stack.Item>
            <Stack.Item>
              <Text as="p">
                <Trans
                  i18nKey="platform.accountDeletion.form.organizationName.confirm.title"
                  values={{
                    name: instanceAddress,
                  }}
                  components={[<b>text</b>]}
                />
              </Text>
            </Stack.Item>
            <Stack.Item previousSpacing="narrow" fill>
              <TextFieldControl
                name="instanceAddress"
              />
            </Stack.Item>
          </Stack>
        </ModalView>
      )}
    />
  );
};

export default withInject<InjectedProps>({
  auditLog: 'AuditLogCrate.auditLog',
  validation: 'FormCrate.validation',
  i18n: 'TranslationCrate.i18n',
  // platform namespace to load translations for RateLimitInterceptor
})(withTranslation(['administration', 'components', 'platform'])(EditAuditLogRetentionModal as any));
